import {
  TFeatureVendor,
  TUseFeatureOptions,
} from '@/console/features/pipelines/hooks/use-feature/use-feature.types';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import {
  FeatureConfigFragment,
  FeatureEnum,
  FeatureMetaDataType,
  FeatureTypeEnum,
  ProjectTypeEnum,
  VendorFragment,
} from '@/shared/types/types.generated';

export const useFeature = (
  feature?: FeatureEnum,
  options?: TUseFeatureOptions,
) => {
  const { data, isLoading } = useReadTenantQuery();
  const vendors = data?.readTenant.vendors || [];

  const hasFeature = (
    vendor: VendorFragment,
    overrideFeature?: FeatureEnum,
  ) => {
    const _feature = overrideFeature ?? feature;
    return vendor?.ides?.some((ide) =>
      ide.features.some(({ feature: featureName }) => featureName === _feature),
    );
  };

  const extractFeatureMetadata = (
    vendor: VendorFragment,
    overrideFeature?: FeatureEnum,
  ) => {
    const _feature = overrideFeature ?? feature;
    if (!_feature) {
      // since feature is optional on the hook, we throw an error to help the developer if it's not defined
      throw new Error(
        'Feature must be provided either on useFeature hook or as override in a function to extract metadata for vendor',
      );
    }

    const relevantIdes = vendor.ides?.filter((ide) =>
      ide.features.some(
        ({ feature: featureName }) => featureName === _feature!,
      ),
    );

    return (
      relevantIdes?.map((ide) => {
        const relevantFeature = ide.features.find(
          ({ feature: featureName }) => featureName === _feature,
        );

        return {
          vendor: vendor.vendorId,
          ide: ide.ideConfigId,
          availableLanguages: ide.availableLanguages,
          feature: _feature,
          metadata: relevantFeature?.metaData as FeatureMetaDataType,
          options: relevantFeature?.options,
        };
      }) || []
    );
  };

  const getVendorFeatures = (
    overrideFeature?: FeatureEnum,
  ): TFeatureVendor[] => {
    const supportedVendors = vendors.filter((vendor) =>
      hasFeature(vendor, overrideFeature),
    );

    return supportedVendors.flatMap((vendor) =>
      extractFeatureMetadata(vendor, overrideFeature),
    );
  };

  const getFeaturesForVendor = (vendorId: string) => {
    const features = getVendorFeatures();

    return features.filter(({ vendor }) => vendor === vendorId);
  };

  const getFeatureOptionsForVendor = (
    vendorId: string,
    ideConfigId: string,
  ) => {
    const features = getVendorFeatures();

    const vendorFeature = features.filter(
      ({ vendor, ide }) => vendor === vendorId && ide === ideConfigId,
    );

    return vendorFeature[0]?.options;
  };

  const getFeatureType = (
    featureTypeOptions?: TUseFeatureOptions,
  ): FeatureTypeEnum | undefined => {
    const _options = featureTypeOptions ?? options;
    const features = getVendorFeatures();

    if (!_options) {
      const featureMetaData = features.find(
        ({ feature: featureName }) => featureName === feature,
      );
      return featureMetaData?.metadata?.featureType;
    }

    const featureMetaData = features.find(
      ({ feature: featureName, vendor, ide }) =>
        featureName === feature &&
        vendor === _options.vendorId &&
        ide === _options.ideConfigId,
    );
    return featureMetaData?.metadata?.featureType;
  };

  const isIdeSupported = (vendorId?: string, ideConfigId?: string): boolean => {
    if (!vendorId || !ideConfigId) {
      return false;
    }

    const features = getFeaturesForVendor(vendorId);

    return features.some((feature) => feature.ide === ideConfigId);
  };

  const getProjectTypesForFeature = (
    overrideFeature?: FeatureEnum,
  ): ProjectTypeEnum[] => {
    const _feature = overrideFeature ?? feature;
    const features = getVendorFeatures(overrideFeature);
    const filteredFeatures = features.filter(
      ({ feature: featureName }) => featureName === _feature,
    );

    const fileExtensionsToDeviceTypesProjectTypes = filteredFeatures?.flatMap(
      (feature) =>
        feature.metadata?.fileExtensionsToDeviceTypes?.map(
          (item) => item.projectType,
        ),
    );
    const fileToFileExtensionsProjectTypes = filteredFeatures?.flatMap(
      (feature) =>
        feature.metadata?.fileToFileExtensions?.map((item) => item.projectType),
    );
    const projectFileExtensionsProjectTypes = filteredFeatures?.flatMap(
      (feature) =>
        feature.metadata?.projectFileExtensions?.map(
          (item) => item.projectType,
        ),
    );

    const projectTypes = [
      ...fileExtensionsToDeviceTypesProjectTypes,
      ...fileToFileExtensionsProjectTypes,
      ...projectFileExtensionsProjectTypes,
    ]?.filter((projectType) => !!projectType);

    if (!projectTypes || projectTypes.length === 0) {
      return Object.values(ProjectTypeEnum);
    }

    return [...new Set(projectTypes)];
  };

  const isLibraryRequired = () => {
    const features = getVendorFeatures();
    const filteredFeatures = features.filter(
      ({ feature: featureName }) => featureName === feature,
    );
    const destinationProjectTypes = filteredFeatures
      ?.flatMap((feature) =>
        feature.metadata?.fileToFileExtensions?.map(
          (item) => item.destinationProjectType,
        ),
      )
      ?.filter((projectType) => !!projectType);

    return destinationProjectTypes?.includes(ProjectTypeEnum.Library);
  };

  const isDeviceFeature = (
    features?: FeatureEnum[],
    isDeviceFeatureOptions?: TUseFeatureOptions,
  ) => {
    const _options = isDeviceFeatureOptions ?? options;
    const allFeatures = (vendors
      ?.flatMap(({ ides }) => ides)
      ?.flatMap((ide) => ide?.features)
      ?.filter(Boolean) ?? []) as FeatureConfigFragment[];

    if (!allFeatures?.length || !features?.length) {
      return false;
    }

    if (!_options) {
      return features?.every(
        (featureName) =>
          allFeatures?.find(({ feature }) => feature === featureName)?.metaData
            ?.featureType === FeatureTypeEnum.DeviceFeature,
      );
    }

    const filteredFeatures = (vendors
      ?.find(({ vendorId }) => vendorId === _options.vendorId)
      ?.ides?.find((ide) => ide.ideConfigId === _options.ideConfigId)
      ?.features ?? []) as FeatureConfigFragment[];

    return features?.every(
      (featureName) =>
        filteredFeatures?.find(({ feature }) => feature === featureName)
          ?.metaData?.featureType === FeatureTypeEnum.DeviceFeature,
    );
  };

  const getAvailableLanguagesForIde = (
    vendorId?: string,
    ideConfigId?: string,
  ) => {
    if (!vendorId || !ideConfigId) {
      return [];
    }

    const features = getVendorFeatures(FeatureEnum.Ideaas);
    const ideaasFeature = features.find(
      ({ feature: featureName, vendor, ide }) =>
        featureName === FeatureEnum.Ideaas &&
        vendor === vendorId &&
        ide === ideConfigId,
    );

    return ideaasFeature?.availableLanguages || [];
  };

  return {
    isLoading,
    featureType: getFeatureType(options),
    supportedVendors: vendors
      .filter((vendor) => hasFeature(vendor))
      .map((vendor) => vendor.vendorId),
    featureVendors: getVendorFeatures(),
    getFeaturesForVendor,
    getFeatureOptionsForVendor,
    isDeviceFeature,
    isIdeSupported,
    projectTypesForFeature: getProjectTypesForFeature(),
    getProjectTypesForFeature,
    isLibraryRequired: isLibraryRequired(),
    getAvailableLanguagesForIde,
    getFeatureType,
  };
};
